import { appClearFormError } from '@actions/app.action';
import { userLogin, userResetPassword, userSignUp } from '@actions/user.action';
import { CommonModule } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Input,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { InputComponent } from '@components/controls/input/input.component';
import { SelectComponent } from '@components/controls/select/select.component';
import { TabsComponent } from '@components/tabs/tabs.component';
import { CultureKeyType, CULTURE_ROUTE_PARAM } from '@constants/app.constants';
import { LetDirective } from '@directives/app-let.directive';
import { DateInputDirective } from '@directives/date-input/date-input.directive';
import { InputDirective } from '@directives/input/input.directive';
import { ErrorType, NullableString } from '@models/app.models';
import { Gender } from '@models/booking.models';
import { CountryPhoneCode } from '@models/cms/app.model';
import { LoginCopy } from '@models/cms/login.model';
import { FeatureFlagsEnum } from '@models/environment.type';
import {
  LoginForm,
  LoginFormConfig,
  ResetPasswordForm,
  ResetPasswordFormConfig,
  SignUpFormConfig,
} from '@models/form/form.models';
import { Store } from '@ngrx/store';
import { selectFormError } from '@selectors/app.selector';
import { selectGenders, selectPhoneCodes } from '@selectors/resource.selector';
import { selectRouteParam } from '@selectors/router.selector';
import { selectLoggedInUser } from '@selectors/user.selector';
import { FeatureFlagService } from '@services/feature-flag/feature-flag.service';
import { LoginFormConfigService } from '@services/form-configs/login-form-config/login-form-config.service';
import { ResetPasswordFormConfigService } from '@services/form-configs/reset-password-form-config/reset-password-form-config.service';
import { SignUpFormConfigService } from '@services/form-configs/sign-up-form-config/sign-up-form-config.service';
import { createPhoneNumberFromParts } from '@utils/data-utils';
import { triggerDisplayFormErrors } from '@utils/form-utils';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { combineLatest, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import {GrowthBookService} from "@services/growthbook/growthbook.service";

@Component({
  selector: 'app-login-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    InputDirective,
    LetDirective,
    TabsComponent,
    DateInputDirective,
    SelectComponent,
  ],
  templateUrl: './login-modal.component.html',
  styleUrls: ['./login-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginModalComponent implements OnInit, OnDestroy {
  genders$ = this.store.select(selectGenders);
  phoneCodes$ = this.store.select(selectPhoneCodes);
  culture$ = this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM));
  onDestroy$ = new Subject<void>();

  activeTab: NullableString = null;

  loginForm: FormGroup<LoginFormConfig> = this.formBuilder.group(
    this.loginFormConfigService.createFormConfig(),
    {
      validators: this.loginFormConfigService.createValidators(),
    },
  );

  resetPasswordForm: FormGroup<ResetPasswordFormConfig> =
    this.formBuilder.group(
      this.resetPasswordFormConfigService.createFormConfig(),
      {
        validators: this.resetPasswordFormConfigService.createValidators(),
      },
    );

  signUpForm: FormGroup<SignUpFormConfig> | null = null;

  get loginFormValues(): LoginForm {
    return this.loginForm.value as LoginForm;
  }

  get resetPasswordFormValues(): ResetPasswordForm {
    return this.resetPasswordForm.value as ResetPasswordForm;
  }

  @Input()
  loginCopy: LoginCopy;
  @Input()
  tabs: { [key: string]: string } = {};
  @Input()
  passwordHints: string[];

  logInError$ = this.store.select(selectFormError);
  loggedInUser$ = this.store.select(selectLoggedInUser);

  submitted = false;

  readonly ErrorType = ErrorType;
  readonly FeatureFlagsEnum = FeatureFlagsEnum;

  constructor(
    public bsModalRef: BsModalRef,
    private store: Store,
    private changeDetector: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private loginFormConfigService: LoginFormConfigService,
    private resetPasswordFormConfigService: ResetPasswordFormConfigService,
    private signUpFormConfigService: SignUpFormConfigService,
    public featureFlagService: FeatureFlagService,
    public gbs: GrowthBookService,
  ) {}

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  ngOnInit(): void {
    this.store.dispatch(appClearFormError());

    this.loggedInUser$.pipe(takeUntil(this.onDestroy$)).subscribe((user) => {
      if (!user) {
        return;
      }
      this.bsModalRef.hide();
    });

    this.activeTab = Object.keys(this.tabs)[0];
    this.changeDetector.detectChanges();

    combineLatest([this.genders$, this.culture$])
      .pipe(first(([g, culture]) => !!g && !!culture))
      .subscribe(([g, culture]) => {
        this.signUpForm = this.formBuilder.group(
          /* eslint-disable @typescript-eslint/no-non-null-assertion */
          this.signUpFormConfigService.createFormConfig({
            genders: g!,
            culture: culture! as CultureKeyType,
          }),
          {
            validators: this.signUpFormConfigService.createValidators(),
          },
        );
        this.changeDetector.detectChanges();
      });

    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    this.logInError$.pipe(takeUntil(this.onDestroy$)).subscribe((err) => {
      if (err?.type === ErrorType.LoginValidationFailure) {
        this.loginForm.get('username')!.setErrors({ incorrect: !!err });
        this.loginForm.get('password')!.setErrors({ incorrect: !!err });
      }
    });
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
  }

  onTabClicked($event: string): void {
    this.activeTab = $event;
  }

  onResetPassword(evt: Event): void {
    evt.preventDefault();

    if (!this.resetPasswordForm.valid) {
      triggerDisplayFormErrors(this.resetPasswordForm);
      return;
    }

    this.activeTab = 'resetSubmitted';

    this.store.dispatch(userResetPassword({ ...this.resetPasswordFormValues }));
  }

  onLogin(evt: Event): void {
    this.submitted = true;
    evt.preventDefault();

    if (!this.loginForm.valid) {
      triggerDisplayFormErrors(this.loginForm);
      return;
    }

    this.store.dispatch(userLogin({ ...this.loginFormValues }));
  }

  onSignUp(phoneCodes: CountryPhoneCode[]): void {
    this.submitted = true;
    if (!this.signUpForm) {
      return;
    }

    if (!this.signUpForm.valid) {
      triggerDisplayFormErrors(this.signUpForm);
      return;
    }

    const phoneCodeNumber = phoneCodes.find(
      (pc) =>
        pc.countryCode === this.signUpForm?.controls.phoneCountryCode.value,
    )?.value;
    if (!phoneCodeNumber) {
      return;
    }

    this.store.dispatch(
      userSignUp({
        email: this.signUpForm.controls.email.value,
        firstName: this.signUpForm.controls.firstName.value,
        lastName: this.signUpForm.controls.lastName.value,
        gender: this.signUpForm.controls.gender.value ?? Gender.XX,
        birthDate: this.signUpForm.controls.birthDate.value,
        password: this.signUpForm.controls.password.value,
        receiveMarketingNotifications:
          this.signUpForm.controls.receiveMarketingNotifications.value,
        phoneNumber: createPhoneNumberFromParts(
          phoneCodeNumber,
          this.signUpForm.controls.phoneNumber.value,
        ),
      }),
    );
  }
}
